import { useState, useEffect, useRef, useCallback } from 'react';
import { BackgroundProcessor, supportsBackgroundProcessors } from '@livekit/track-processors';

export const TEACHER_BACKGROUNDS = [
  {
    id: 'office',
    label: 'Office Studio',
    description: 'Professional modern studio',
    thumbnail: '/backgrounds/office_landscape.webp',
    type: 'image',
    landscapePath: '/backgrounds/office_landscape.webp',
    portraitPath: '/backgrounds/office_portrait.webp',
  },
  {
    id: 'blur',
    label: 'Blur',
    description: 'Soft blurred background',
    thumbnail: null,
    type: 'blur',
  },
  {
    id: 'none',
    label: 'None',
    description: 'Real camera background',
    thumbnail: null,
    type: 'none',
    isDefault: true,
  },
];

export const STUDENT_BACKGROUNDS = [
  {
    id: 'study',
    label: 'Study Space',
    description: 'Cozy modern study space',
    thumbnail: '/backgrounds/student_landscape.webp',
    type: 'image',
    landscapePath: '/backgrounds/student_landscape.webp',
    portraitPath: '/backgrounds/student_portrait.webp',
  },
  {
    id: 'blur',
    label: 'Blur',
    description: 'Soft blurred background',
    thumbnail: null,
    type: 'blur',
  },
  {
    id: 'none',
    label: 'None',
    description: 'Real camera background',
    thumbnail: null,
    type: 'none',
    isDefault: true,
  },
];

// Backward compatibility export
export const THEMED_BACKGROUNDS = TEACHER_BACKGROUNDS;

// Google MediaPipe's landscape-optimized model: 144x256 (vs 256x256), uses ~44% fewer pixels per frame,
// providing 2-3x faster inference and dramatically lower CPU/GPU load on laptops and mobile devices.
const FAST_LANDSCAPE_MODEL =
  'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter_landscape/float16/latest/selfie_segmenter_landscape.tflite';

/**
 * Determines current orientation based on camera track settings or window viewport
 */
export function getTrackOrientation(track) {
  try {
    const mediaTrack = track?.mediaStreamTrack;
    if (mediaTrack && typeof mediaTrack.getSettings === 'function') {
      const settings = mediaTrack.getSettings();
      if (settings.width && settings.height) {
        return settings.width < settings.height ? 'portrait' : 'landscape';
      }
    }
  } catch (_) {}

  if (typeof window !== 'undefined') {
    if (window.screen?.orientation?.type?.includes('portrait')) {
      return 'portrait';
    }
    if (window.innerHeight > window.innerWidth) {
      return 'portrait';
    }
  }
  return 'landscape';
}

export function useVirtualBackground({ localVideoTrack, onToast, role = 'teacher' }) {
  const isStudent = role === 'student';
  const availableBackgrounds = isStudent ? STUDENT_BACKGROUNDS : TEACHER_BACKGROUNDS;
  const defaultBgId = 'none';
  const storageKey = isStudent ? 'budlee_liveclass_virtual_bg_student' : 'budlee_liveclass_virtual_bg_teacher';

  const [selectedBgId, setSelectedBgId] = useState(() => {
    if (typeof localStorage !== 'undefined') {
      const saved = isStudent
        ? localStorage.getItem('budlee_liveclass_virtual_bg_student')
        : (localStorage.getItem('budlee_liveclass_virtual_bg_teacher') || localStorage.getItem('budlee_liveclass_virtual_bg'));
      if (saved) {
        const isValid = availableBackgrounds.some((b) => b.id === saved);
        if (isValid) return saved;
      }
    }
    return defaultBgId;
  });

  const [isApplying, setIsApplying] = useState(false);
  const [isSupported, setIsSupported] = useState(true);

  // Stable references
  const onToastRef = useRef(onToast);
  useEffect(() => {
    onToastRef.current = onToast;
  }, [onToast]);

  const selectedBgIdRef = useRef(selectedBgId);
  useEffect(() => {
    selectedBgIdRef.current = selectedBgId;
  }, [selectedBgId]);

  const processorRef = useRef(null);
  const activeTrackRef = useRef(null);
  const appliedTrackIdRef = useRef(null);
  const appliedBgIdRef = useRef(null);
  const appliedImagePathRef = useRef(null);
  const isApplyingRef = useRef(false);
  const wasBypassedForBackgroundRef = useRef(false);
  const initTimerRef = useRef(null);
  const resizeDebounceRef = useRef(null);

  // Stable ref for available backgrounds
  const availableBackgroundsRef = useRef(availableBackgrounds);
  useEffect(() => {
    availableBackgroundsRef.current = availableBackgrounds;
  }, [availableBackgrounds]);

  // Preload background images into browser cache on mount to eliminate image decode delay
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const defaultBg = availableBackgrounds.find((b) => b.id === defaultBgId);
      if (defaultBg?.landscapePath) {
        const p1 = new Image();
        p1.src = defaultBg.landscapePath;
      }
      if (defaultBg?.portraitPath) {
        const p2 = new Image();
        p2.src = defaultBg.portraitPath;
      }
    }
  }, [availableBackgrounds, defaultBgId]);

  // Check hardware/browser support on mount
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && typeof supportsBackgroundProcessors === 'function') {
        const supported = supportsBackgroundProcessors();
        setIsSupported(Boolean(supported));
      }
    } catch (e) {
      console.warn('[VirtualBackground] Failed to check processor support:', e);
      setIsSupported(true);
    }
  }, []);

  // Get stable track ID
  const getTrackId = (track) => {
    if (!track) return null;
    return track.sid || track.id || track.mediaStreamTrack?.id || null;
  };

  // Internal helper to apply background to track safely
  const applyToTrack = useCallback(async (track, bgId) => {
    if (!track) return;
    const trackId = getTrackId(track);

    // If already applied on this exact track, skip
    if (
      appliedTrackIdRef.current === trackId &&
      appliedBgIdRef.current === bgId &&
      (bgId === 'none' || processorRef.current)
    ) {
      return;
    }

    // Mutex lock to prevent overlapping executions
    if (isApplyingRef.current) {
      return;
    }
    isApplyingRef.current = true;
    setIsApplying(true);

    try {
      if (bgId === 'none') {
        if (processorRef.current && typeof processorRef.current.switchTo === 'function') {
          try {
            await processorRef.current.switchTo({ mode: 'disabled' });
          } catch (switchErr) {
            console.warn('[VirtualBackground] Error switching to disabled mode, trying stopProcessor:', switchErr);
            try {
              await track.stopProcessor?.();
            } catch (_) {}
            processorRef.current = null;
          }
        } else if (track && typeof track.stopProcessor === 'function') {
          try {
            await track.stopProcessor();
          } catch (e) {
            console.warn('[VirtualBackground] Error stopping processor:', e);
          }
          processorRef.current = null;
        }
        appliedTrackIdRef.current = trackId;
        appliedBgIdRef.current = 'none';
        appliedImagePathRef.current = null;
        return;
      }

      const config = availableBackgroundsRef.current.find((bg) => bg.id === bgId);
      if (!config) return;

      const commonAssetPaths = {
        modelAssetPath: FAST_LANDSCAPE_MODEL,
      };

      if (config.type === 'blur') {
        if (processorRef.current && typeof processorRef.current.switchTo === 'function') {
          await processorRef.current.switchTo({
            mode: 'background-blur',
            blurRadius: 15,
          });
        } else {
          const newProcessor = BackgroundProcessor({
            mode: 'background-blur',
            blurRadius: 15,
            assetPaths: commonAssetPaths,
          });
          await track.setProcessor(newProcessor);
          processorRef.current = newProcessor;
        }
        appliedTrackIdRef.current = trackId;
        appliedBgIdRef.current = 'blur';
        appliedImagePathRef.current = null;
      } else if (config.type === 'image') {
        const orientation = getTrackOrientation(track);
        const imagePath = orientation === 'portrait' ? config.portraitPath : config.landscapePath;

        if (processorRef.current && typeof processorRef.current.switchTo === 'function') {
          await processorRef.current.switchTo({
            mode: 'virtual-background',
            imagePath,
          });
        } else {
          const newProcessor = BackgroundProcessor({
            mode: 'virtual-background',
            imagePath,
            assetPaths: commonAssetPaths,
          });
          await track.setProcessor(newProcessor);
          processorRef.current = newProcessor;
        }
        appliedTrackIdRef.current = trackId;
        appliedBgIdRef.current = config.id;
        appliedImagePathRef.current = imagePath;
      }
    } catch (err) {
      console.error('[VirtualBackground] Failed to apply background:', err);
      // Clean fallback: stop processor and reset to 'none' without hanging the camera
      try {
        await track.stopProcessor?.();
      } catch (_) {}
      processorRef.current = null;
      appliedImagePathRef.current = null;
      appliedBgIdRef.current = 'none';
      appliedTrackIdRef.current = trackId;
      setSelectedBgId('none');
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(storageKey, 'none');
      }
      onToastRef.current?.('Unable to start virtual background on this device. Reverted to normal camera.', 'error');
    } finally {
      isApplyingRef.current = false;
      setIsApplying(false);
    }
  }, [storageKey]);

  // Track attachment effect - runs ONLY when localVideoTrack reference changes
  useEffect(() => {
    if (!localVideoTrack) {
      // Camera is muted/off: clear active processor cleanly
      if (initTimerRef.current) {
        clearTimeout(initTimerRef.current);
        initTimerRef.current = null;
      }
      if (processorRef.current && activeTrackRef.current) {
        activeTrackRef.current.stopProcessor?.().catch(() => {});
      }
      processorRef.current = null;
      activeTrackRef.current = null;
      appliedTrackIdRef.current = null;
      appliedBgIdRef.current = null;
      appliedImagePathRef.current = null;
      return;
    }

    activeTrackRef.current = localVideoTrack;
    const currentSelected = selectedBgIdRef.current;

    if (currentSelected && currentSelected !== 'none') {
      // Give camera and WebRTC connection 900ms to stabilize before spinning up the AI segmentation processor.
      // This prevents CPU/GPU spikes during initial room entry and ensures smooth call setup.
      initTimerRef.current = setTimeout(() => {
        if (activeTrackRef.current) {
          applyToTrack(activeTrackRef.current, currentSelected);
        }
      }, 900);
    }

    return () => {
      if (initTimerRef.current) {
        clearTimeout(initTimerRef.current);
        initTimerRef.current = null;
      }
    };
  }, [localVideoTrack, applyToTrack]);

  // Debounced listener for device rotation / window resize
  useEffect(() => {
    const handleOrientationOrResize = () => {
      if (resizeDebounceRef.current) {
        clearTimeout(resizeDebounceRef.current);
      }

      resizeDebounceRef.current = setTimeout(() => {
        const track = activeTrackRef.current;
        const currentBgId = selectedBgIdRef.current;
        if (!track || !processorRef.current || currentBgId === 'none' || currentBgId === 'blur' || isApplyingRef.current) {
          return;
        }

        const config = availableBackgroundsRef.current.find((bg) => bg.id === currentBgId);
        if (!config || config.type !== 'image') return;

        const orientation = getTrackOrientation(track);
        const targetPath = orientation === 'portrait' ? config.portraitPath : config.landscapePath;

        if (appliedImagePathRef.current !== targetPath) {
          processorRef.current
            .switchTo?.({
              mode: 'virtual-background',
              imagePath: targetPath,
            })
            .then(() => {
              appliedImagePathRef.current = targetPath;
            })
            .catch((err) => {
              console.warn('[VirtualBackground] Failed to switch background orientation:', err);
            });
        }
      }, 250);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleOrientationOrResize);
      window.addEventListener('orientationchange', handleOrientationOrResize);
      return () => {
        if (resizeDebounceRef.current) {
          clearTimeout(resizeDebounceRef.current);
        }
        window.removeEventListener('resize', handleOrientationOrResize);
        window.removeEventListener('orientationchange', handleOrientationOrResize);
      };
    }
  }, []);

  // Handle tab/window minimization: temporarily bypass AI processor to raw camera track
  // to prevent WebGL/requestVideoFrameCallback stalling while hidden, then re-apply on restore.
  useEffect(() => {
    const handleVisibilityChange = async () => {
      const track = activeTrackRef.current;
      if (!track) return;

      if (document.visibilityState === 'hidden') {
        if (processorRef.current && appliedBgIdRef.current && appliedBgIdRef.current !== 'none') {
          wasBypassedForBackgroundRef.current = true;
          try {
            await track.stopProcessor?.();
          } catch (_) {}
          processorRef.current = null;
          appliedTrackIdRef.current = null;
          appliedBgIdRef.current = 'none';
          appliedImagePathRef.current = null;
        }
      } else if (document.visibilityState === 'visible') {
        if (wasBypassedForBackgroundRef.current) {
          wasBypassedForBackgroundRef.current = false;
          const currentBg = selectedBgIdRef.current;
          if (currentBg && currentBg !== 'none') {
            await applyToTrack(track, currentBg);
          }
        }
      }
    };

    if (typeof window !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [applyToTrack]);

  // User-facing selection handler
  const setBackground = useCallback(async (bgId) => {
    setSelectedBgId(bgId);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(storageKey, bgId);
    }

    if (activeTrackRef.current) {
      await applyToTrack(activeTrackRef.current, bgId);
    }
  }, [applyToTrack, storageKey]);

  return {
    selectedBgId,
    setBackground,
    isApplying,
    isSupported,
    availableBackgrounds,
    defaultBgId,
  };
}
